import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCalendar, FiX } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Calendar from './Calendar';
import { addDays, formatDateWithWeekday } from '../utils/dateHelpers';

function DatePicker({
  selectedDate,
  onChange,
  placeholder = "Select a date",
  className = "",
  disabled = false,
  maxDays = 30,
  minDays = 0,
  quickSchedule = false
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isMobile, setIsMobile] = useState(false);

  // For quick schedule, allow 12 months (365 days)
  const effectiveMaxDays = quickSchedule ? 365 : maxDays;

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Initialize current month when picker opens
  useEffect(() => {
    if (isOpen) {
      // Set current month to selected date or current month
      if (selectedDate) {
        setCurrentMonth(new Date(selectedDate));
      } else {
        setCurrentMonth(new Date());
      }
    }
  }, [isOpen, selectedDate]);

  // Validate date is within allowed range
  const isDateValid = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const minDate = addDays(today, minDays);
    const maxDate = addDays(today, effectiveMaxDays);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);

    return checkDate >= minDate && checkDate <= maxDate;
  };

  const handleDateSelect = (date) => {
    if (isDateValid(date)) {
      if (quickSchedule) {
        // In quick schedule mode, immediately apply and close
        onChange(date);
        setIsOpen(false);
      } else {
        // Legacy behaviour for non-quick schedule
        onChange(date);
        setIsOpen(false);
      }
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const displayText = selectedDate
    ? formatDateWithWeekday(selectedDate)
    : placeholder;

  return (
    <div className={`relative ${className}`}>
      {/* Trigger Button */}
      <motion.div
        whileHover={{ scale: disabled ? 1 : 1.02 }}
        whileTap={{ scale: disabled ? 1 : 0.98 }}
        onClick={() => !disabled && setIsOpen(true)}
        className={`border border-border-subtle rounded-xl px-4 py-3 cursor-pointer transition-all duration-200 min-h-[52px] ${
          disabled 
            ? 'bg-surface cursor-not-allowed' 
            : 'bg-surface focus:bg-card hover:border-accent hover:'
        } ${isOpen ? 'border-accent  bg-card' : ''}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className={`font-medium ${
              selectedDate ? 'text-text-primary' : 'text-text-muted'
            }`}>
              {displayText}
            </p>
          </div>
          <SafeIcon 
            icon={FiCalendar} 
            className={`w-5 h-5 ${disabled ? 'text-text-muted' : 'text-text-muted'}`} 
          />
        </div>
      </motion.div>

      {/* Calendar Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={`fixed inset-0 bg-black bg-opacity-50 z-[60] ${
              isMobile
                ? 'flex items-end pb-16'
                : 'flex items-center justify-center p-4'
            }`}
            onClick={handleClose}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: isMobile ? 50 : 0 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: isMobile ? 50 : 0 }}
              className={`bg-card shadow-modal w-full overflow-hidden flex flex-col relative ${
                isMobile
                  ? 'rounded-t-xl max-h-[calc(100vh-4rem)] w-full'
                  : 'rounded-xl max-w-md max-h-[85vh] mx-auto'
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button - positioned absolutely */}
              <button
                type="button"
                onClick={handleClose}
                className="absolute top-3 right-3 w-10 h-10 bg-surface rounded-xl flex items-center justify-center hover:bg-card transition-colors duration-200 z-10"
                aria-label="Close date picker"
              >
                <SafeIcon icon={FiX} className="w-5 h-5 text-text-secondary" />
              </button>

              {/* Calendar */}
              <div className={`px-4 sm:px-6 pt-14 overflow-y-auto flex-1 min-h-0 ${
                isMobile ? 'pb-6' : 'pb-4'
              }`}>
                <div className="flex justify-center">
                  <Calendar
                    currentMonth={currentMonth}
                    onMonthChange={setCurrentMonth}
                    startDate={selectedDate}
                    endDate={selectedDate}
                    onDateSelect={handleDateSelect}
                    minDate={addDays(new Date(), minDays)}
                    maxDate={addDays(new Date(), effectiveMaxDays)}
                  />
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default DatePicker;